import type { AlbumSummary, CatalogServiceInfo } from '@/api/types'
import { CollectionCard } from '@/components/CollectionCard'
import { Cover } from '@/components/Cover'
import { Skeleton } from '@/components/ui'
import { cn } from '@/lib/cn'

import { plural } from './bulkResult'
import { providerLabel } from './ProviderFilter'

function metaLine(album: AlbumSummary, providerName?: string): string {
  const parts: string[] = []
  if (album.release_year !== null) parts.push(String(album.release_year))
  if (album.record_type) parts.push(album.record_type === 'ep' ? 'EP' : album.record_type.charAt(0).toUpperCase() + album.record_type.slice(1))
  if (album.total_tracks !== null) parts.push(plural(album.total_tracks, 'song', 'songs'))
  if (providerName) parts.push(providerName)
  return parts.join(' · ')
}

export interface AlbumCardProps {
  album: AlbumSummary
  /** Shown in the meta line when more than one provider returns albums. */
  providerName?: string
  onSelect: () => void
}

export function AlbumCard({ album, providerName, onSelect }: AlbumCardProps) {
  const meta = metaLine(album, providerName)

  return (
    <CollectionCard onClick={onSelect} artwork={<Cover src={album.artwork_url} size="xl" />} title={album.title} subtitle={album.artist_name} metadata={meta} />
  )
}

export interface AlbumGridProps {
  albums: AlbumSummary[]
  providers: CatalogServiceInfo[]
  /** True when the list mixes providers and the source is worth naming. */
  showProvider?: boolean
  onSelect: (album: AlbumSummary) => void
  className?: string
}

export function AlbumGrid({
  albums,
  providers,
  showProvider = false,
  onSelect,
  className,
}: AlbumGridProps) {
  return (
    <ul className={cn('grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4', className)}>
      {albums.map((album) => (
        <li key={`${album.provider}:${album.external_id}`}>
          <AlbumCard
            album={album}
            providerName={showProvider ? providerLabel(providers, album.provider) : undefined}
            onSelect={() => onSelect(album)}
          />
        </li>
      ))}
    </ul>
  )
}

/** Same box model as a real card, so the grid does not jump when data lands. */
export function AlbumGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div
      role="status"
      aria-label="Loading albums"
      className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4"
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="flex flex-col gap-2 p-2">
          <Skeleton className="aspect-square w-full rounded-card" />
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-2.5 w-2/5" />
          </div>
        </div>
      ))}
    </div>
  )
}
